import type { IconType } from 'react-icons'
import { formatDateOnly as formatDateOnlyUtil } from '@/lib/utils/date-only'
import {
    MdCancel,
    MdCheckCircle,
    MdInfo,
    MdSchedule,
} from 'react-icons/md'

/**
 * Formatadores e mapas de status da tela de conta.
 *
 * Estavam declarados dentro do componente, e três deles apareciam DUAS vezes
 * com o mesmo nome e comportamentos diferentes — `formatDate` com e sem hora,
 * `getStatusConfig` para cobrança e para assinatura. Aqui cada um tem nome
 * próprio, para que a escolha entre eles seja explícita em vez de depender de
 * qual declaração estava mais perto no arquivo.
 */

export interface StatusConfig {
    label: string
    className: string
    icon: IconType
}

/** Máscara progressiva de CPF (000.000.000-00). */
export function formatCpf(value: string): string {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 3) return numbers
    if (numbers.length <= 6)
        return `${numbers.slice(0, 3)}.${numbers.slice(3)}`
    if (numbers.length <= 9)
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`
}

/** Máscara progressiva de CNPJ (00.000.000/0000-00). */
export function formatCnpj(value: string): string {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length > 14) return value
    if (numbers.length <= 2) return numbers
    if (numbers.length <= 5)
        return `${numbers.slice(0, 2)}.${numbers.slice(2)}`
    if (numbers.length <= 8)
        return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`
    if (numbers.length <= 12)
        return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`
}

/** Máscara progressiva de telefone, fixo ou celular. */
export function formatPhone(value: string): string {
    const numbers = value.replace(/\D/g, '').slice(0, 11)
    if (numbers.length <= 2) return numbers
    if (numbers.length <= 7)
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
    if (numbers.length <= 10)
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
}

/** Máscara progressiva de CEP (00000-000). */
export function formatCep(value: string): string {
    const cleanValue = value.replace(/\D+/g, '')
    if (cleanValue.length <= 5) return cleanValue
    return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5, 8)}`
}

/** Centavos para moeda brasileira. */
export function formatPrice(cents: number): string {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)
}

/** Data com hora — usada nas listas de cobrança. */
export function formatDateTime(dateString: string): string {
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(dateString))
}

/** Só a data — usada no cartão da assinatura, onde a hora não importa. */
export function formatDateOnly(dateString: string): string {
    return formatDateOnlyUtil(dateString)
}

/** Status de uma COBRANÇA (o que o gateway devolve). */
export function getBillingStatusConfig(status: string): StatusConfig {
    const statusMap: Record<string, StatusConfig> = {
        PENDING: {
            label: 'Pendente',
            className: 'badge-warning',
            icon: MdSchedule,
        },
        RECEIVED: {
            label: 'Recebido',
            className: 'badge-success',
            icon: MdCheckCircle,
        },
        CONFIRMED: {
            label: 'Confirmado',
            className: 'badge-success',
            icon: MdCheckCircle,
        },
        OVERDUE: {
            label: 'Vencido',
            className: 'badge-error',
            icon: MdCancel,
        },
        REFUNDED: {
            label: 'Reembolsado',
            className: 'badge-info',
            icon: MdInfo,
        },
        RECEIVED_IN_CASH: {
            label: 'Recebido em Dinheiro',
            className: 'badge-success',
            icon: MdCheckCircle,
        },
    }
    return (
        statusMap[status] || {
            label: status,
            className: 'badge-ghost',
            icon: MdInfo,
        }
    )
}

/** Status da ASSINATURA — conjunto de valores diferente do de cobrança. */
export function getSubscriptionStatusConfig(status: string): StatusConfig {
    switch (status) {
        case 'ACTIVE':
            return {
                label: 'Ativa',
                className: 'badge-success',
                icon: MdCheckCircle,
            }
        case 'SUSPENDED':
            return {
                label: 'Suspensa',
                className: 'badge-error !text-white',
                icon: MdCancel,
            }
        default:
            return {
                label: status,
                className: 'badge-ghost',
                icon: MdInfo,
            }
    }
}
